import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/common";
import { SearchableTradesSelect } from "@/features/admin/jobs/components/SearchableTradesSelect";
import { AUSTRALIAN_STATES } from "@/data/australian-suburbs";
import styles from "./ShoppingCentreDetailsForm.module.css";
export const ShoppingCentreDetailsForm = ({ centre, onSave, saveHandlerRef, }) => {
    const [scName, setScName] = useState("");
    const [state, setState] = useState("");
    const [landlordName, setLandlordName] = useState("");
    const [numberOfTenancies, setNumberOfTenancies] = useState("");
    const [sqMeterage, setSqMeterage] = useState("");
    const [contactId, setContactId] = useState("");
    const [location, setLocation] = useState("");
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (centre) {
            setScName(centre.scName ?? "");
            setState(centre.state ?? "");
            setLandlordName(centre.landlordName ?? "");
            setNumberOfTenancies(centre.numberOfTenancies == null || centre.numberOfTenancies === 0
                ? ""
                : String(centre.numberOfTenancies));
            setSqMeterage(centre.sqMeterage == null || centre.sqMeterage === 0
                ? ""
                : String(centre.sqMeterage));
            setContactId(centre.contactId ?? "");
            setLocation(centre.location ?? "");
        }
        else {
            setScName("");
            setState("");
            setLandlordName("");
            setNumberOfTenancies("");
            setSqMeterage("");
            setContactId("");
            setLocation("");
        }
        setErrors({});
    }, [centre]);
    const validate = () => {
        const next = {};
        if (!scName.trim())
            next.scName = "SC Name is required";
        const tenanciesNum = numberOfTenancies === "" ? null : parseInt(numberOfTenancies, 10);
        const sqMeterageNum = sqMeterage === "" ? null : parseFloat(sqMeterage);
        if (tenanciesNum != null && !isNaN(tenanciesNum) && tenanciesNum < 0)
            next.numberOfTenancies = "Must be 0 or greater";
        if (sqMeterageNum != null && !isNaN(sqMeterageNum) && sqMeterageNum < 0)
            next.sqMeterage = "Must be 0 or greater";
        setErrors(next);
        return Object.keys(next).length === 0;
    };
    const handleSave = useCallback(async () => {
        if (!validate())
            return undefined;
        const tenanciesNum = parseInt(numberOfTenancies, 10);
        const sqMeterageNum = parseFloat(sqMeterage);
        const tenanciesVal = numberOfTenancies === ""
            ? null
            : isNaN(tenanciesNum)
                ? null
                : tenanciesNum;
        const sqMeterageVal = sqMeterage === "" ? null : isNaN(sqMeterageNum) ? null : sqMeterageNum;
        const dto = {
            scName: scName.trim(),
            landlordName: landlordName.trim() || null,
            contactId: contactId.trim() || null,
            location: location.trim() || null,
            numberOfTenancies: tenanciesVal,
            sqMeterage: sqMeterageVal,
            state: state || null,
        };
        return onSave(dto);
    }, [
        scName,
        state,
        landlordName,
        contactId,
        location,
        numberOfTenancies,
        sqMeterage,
        onSave,
    ]);
    useEffect(() => {
        saveHandlerRef.current = handleSave;
        return () => {
            saveHandlerRef.current = null;
        };
    }, [saveHandlerRef, handleSave]);
    return (_jsx("div", { className: styles.formContainer, children: _jsxs("div", { className: styles.paper, children: [_jsx("h3", { className: styles.sectionTitle, children: "Shopping Centre Details" }), _jsxs("div", { className: styles.formGrid, children: [_jsxs("div", { className: styles.leftColumn, children: [_jsxs("div", { className: styles.formGroupVertical, children: [_jsxs("label", { className: styles.label, htmlFor: "sc-name", children: ["SC Name ", _jsx("span", { className: styles.required, children: "*" })] }), _jsx(Input, { id: "sc-name", type: "text", value: scName, onChange: (e) => setScName(e.target.value), error: errors.scName, placeholder: "Shopping centre name" })] }), _jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "state", children: "State" }), _jsxs("div", { children: [_jsx("div", { className: styles.formControl, children: _jsxs("select", { id: "state", className: styles.select, value: state, onChange: (e) => setState(e.target.value), children: [_jsx("option", { value: "", children: "Select state" }), AUSTRALIAN_STATES.map((s) => (_jsx("option", { value: s.code, children: s.code }, s.code)))] }) }), errors.state && (_jsx("span", { className: styles.errorText, children: errors.state }))] })] }), _jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "landlord-name", children: "Landlord Name" }), _jsx(Input, { id: "landlord-name", type: "text", value: landlordName, onChange: (e) => setLandlordName(e.target.value), placeholder: "Landlord name" })] }), _jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "shopping-centre-contact", children: "Contact" }), _jsx("div", { className: styles.contactField, children: _jsx(SearchableTradesSelect, { value: contactId, onChange: setContactId, placeholder: "Type here to search" }) })] })] }), _jsxs("div", { className: styles.rightColumn, children: [_jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "tenancies", children: "Number of Tenancies" }), _jsx(Input, { id: "tenancies", type: "number", min: 0, value: numberOfTenancies, onChange: (e) => setNumberOfTenancies(e.target.value), error: errors.numberOfTenancies, placeholder: "" })] }), _jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "sq-meterage", children: "Sq Meterage" }), _jsx(Input, { id: "sq-meterage", type: "number", min: 0, value: sqMeterage, onChange: (e) => setSqMeterage(e.target.value), error: errors.sqMeterage, placeholder: "" })] }), _jsxs("div", { className: styles.formGroupVertical, children: [_jsx("label", { className: styles.label, htmlFor: "shopping-centre-location", children: "Location" }), _jsx(Input, { id: "shopping-centre-location", type: "text", value: location, onChange: (e) => setLocation(e.target.value), placeholder: "Location" })] })] })] })] }) }));
};
